import React, { useState, useEffect, useCallback } from 'react';
import { Card, Button, Badge, LoadingSkeleton } from './ui';

const classes = ['Form 1', 'Form 2', 'Form 3', 'Form 4'];

function Communication({ token }) {
  const [students, setStudents] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    recipient_type: 'all_students', subject: '', message: '', class_name: '', recipient_id: ''
  });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [studentsRes, teachersRes, logsRes] = await Promise.all([
        fetch('/api/v1/students', { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch('/api/v1/teachers', { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch('/api/v1/communication/logs', { headers: { 'Authorization': `Bearer ${token}` } })
      ]);
      if (studentsRes.ok) setStudents(await studentsRes.json());
      if (teachersRes.ok) setTeachers(await teachersRes.json());
      if (logsRes.ok) setLogs(await logsRes.json());
    } catch {
      setError('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { if (token) fetchData(); }, [token, fetchData]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!formData.subject.trim()) { setError('Subject is required'); return; }
    if (!formData.message.trim()) { setError('Message is required'); return; }
    if (formData.recipient_type === 'specific' && !formData.recipient_id) { setError('Please select a recipient'); return; }
    if (formData.recipient_type === 'class' && !formData.class_name) { setError('Please select a class'); return; }
    setLoading(true);

    try {
      const response = await fetch('/api/v1/communication/send', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ ...formData, message_type: 'email' })
      });
      const result = await response.json();
      if (response.ok) {
        setFormData({ recipient_type: 'all_students', subject: '', message: '', class_name: '', recipient_id: '' });
        fetchData();
      } else {
        setError(result.error || 'Failed to send email');
      }
    } catch {
      setError('Error sending email');
    } finally {
      setLoading(false);
    }
  };

  const templates = [
    { title: 'Fee Reminder', subject: 'Fee Payment Reminder', message: 'Dear Parent/Guardian,\n\nThis is a friendly reminder that school fees are due. Please ensure payment is made by the due date to avoid penalties.\n\nIf you have already made the payment, please disregard this message.\n\nThank you for your cooperation.\n\nBest regards,\nBest School Administration' },
    { title: 'Event Announcement', subject: 'School Event Announcement', message: 'Dear Parents/Guardians,\n\nWe are pleased to invite you to our upcoming school event. Details will be shared soon.\n\nYour presence would be greatly appreciated.\n\nBest regards,\nBest School' },
    { title: 'Exam Results', subject: 'Exam Results Available', message: 'Dear Parent/Guardian,\n\nYour child\'s examination results have been released. Please log in to the school portal to view the results.\n\nIf you need assistance, please contact the school administration.\n\nRegards,\nBest School' },
  ];

  return (
    <div>
      <h2 style={{ fontSize: '1.25rem', fontWeight: 700, color: 'var(--color-text)', marginBottom: '1rem' }}>Email Communication</h2>

      {error && <div className="alert alert-error">{error}</div>}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1.5rem' }}>
        <Card title="Send Email">
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label>Recipient Type</label>
              <select value={formData.recipient_type} onChange={(e) => setFormData({...formData, recipient_type: e.target.value, recipient_id: '', class_name: ''})}>
                <option value="all_students">All Students (Parents)</option>
                <option value="all_teachers">All Teachers</option>
                <option value="class">Specific Class</option>
                <option value="specific">Specific Person</option>
              </select>
            </div>

            {formData.recipient_type === 'class' && (
              <div className="form-group">
                <label>Select Class</label>
                <select value={formData.class_name} onChange={(e) => setFormData({...formData, class_name: e.target.value})} required>
                  <option value="">Choose Class</option>
                  {classes.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
            )}

            {formData.recipient_type === 'specific' && (
              <div className="form-group">
                <label>Select Recipient</label>
                <select value={formData.recipient_id} onChange={(e) => setFormData({...formData, recipient_id: e.target.value})} required>
                  <option value="">Choose Recipient</option>
                  <optgroup label="Students">
                    {students.map(s => <option key={`s-${s.id}`} value={`student_${s.id}`}>{s.full_name} — {s.guardian_email || 'No email'}</option>)}
                  </optgroup>
                  <optgroup label="Teachers">
                    {teachers.map(t => <option key={`t-${t.id}`} value={`teacher_${t.id}`}>{t.full_name} — {t.email || 'No email'}</option>)}
                  </optgroup>
                </select>
              </div>
            )}

            <div className="form-group">
              <label>Subject *</label>
              <input type="text" value={formData.subject} onChange={(e) => setFormData({...formData, subject: e.target.value})} required />
            </div>

            <div className="form-group">
              <label>Message *</label>
              <textarea value={formData.message} onChange={(e) => setFormData({...formData, message: e.target.value})} rows="6" required />
            </div>

            <Button type="submit" variant="primary" disabled={loading}>
              {loading ? 'Sending...' : 'Send Email'}
            </Button>
          </form>
        </Card>

        <Card title="Recent Email Logs">
          {loading && logs.length === 0 ? (
            <LoadingSkeleton type="text" rows={5} />
          ) : logs.length === 0 ? (
            <p style={{ color: 'var(--color-text-muted)', textAlign: 'center', padding: '2rem' }}>No emails sent yet.</p>
          ) : (
            <div style={{ maxHeight: '500px', overflowY: 'auto' }}>
              {logs.map(log => (
                <div key={log.id} style={{ padding: '0.75rem', borderBottom: '1px solid var(--color-border-light)', background: log.status === 'sent' ? '#F0F7FF' : '#FFF3CD', borderRadius: 'var(--radius)', marginBottom: '0.5rem' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.25rem' }}>
                    <strong style={{ fontSize: '0.9rem' }}>{log.subject}</strong>
                    <Badge variant={log.status === 'sent' ? 'success' : 'warning'}>{log.status}</Badge>
                  </div>
                  <div style={{ fontSize: '0.8rem', color: 'var(--color-text-muted)' }}>
                    <div>To: {log.recipient_name || 'N/A'}</div>
                    {log.recipient_email && <div>Email: {log.recipient_email}</div>}
                    <div>{new Date(log.sent_at).toLocaleString()}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>

      <Card title="Email Templates" style={{ marginTop: '1.5rem' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1rem' }}>
          {templates.map((t, i) => (
            <div key={i} style={{ background: 'var(--color-bg)', borderRadius: 'var(--radius)', padding: '1rem' }}>
              <h4 style={{ fontSize: '0.9rem', fontWeight: 600, marginBottom: '0.5rem' }}>{t.title}</h4>
              <p style={{ fontSize: '0.8rem', color: 'var(--color-text-muted)' }}>{t.message.substring(0, 80)}...</p>
              <Button variant="secondary" size="sm" onClick={() => setFormData({ ...formData, subject: t.subject, message: t.message })} style={{ marginTop: '0.5rem' }}>
                Use Template
              </Button>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}

export default Communication;
